import Swiper from "swiper";
import { SliderComponent } from "./slider.component";
import inViewport from "../../utilities/inViewport";

interface ISwiper extends Swiper {
    controller: object;
    lazy: object;
    realIndex: number;
    previousIndex: number;

    init(): void;

    on(event: string, callback: () => void);
}

export class SliderFullscreenComponent extends SliderComponent {
    public static SELECTOR = ".slider--fullscreen";
    public static THUMBNAIL_SELECTOR = ".slider__thumbnail";
    public static THUMBNAILS_CONTAINER_SELECTOR = ".slider__thumbnails__container";
    public static FULLSCREEN_BUTTON_SELECTOR = ".slider__fullscreen-button";
    public static CLOSE_BUTTON_SELECTOR = ".slider__close-button";
    public static THUMBNAILS_PREV_BUTTON_SELECTOR = ".slider__thumbnails__button-prev";
    public static THUMBNAILS_NEXT_BUTTON_SELECTOR = ".slider__thumbnails__button-next";
    public static NAVIGATION_SELECTOR = ".navigation";

    public static autoInit(): SliderFullscreenComponent[] {
        const sliders = document.querySelectorAll(this.SELECTOR) as NodeListOf<HTMLElement>;
        return Array.from(sliders).map((slider) => new this(slider));
    }

    private sliderThumbnailsElement: HTMLElement;
    private sliderThumbnailsContainer: HTMLElement;
    private sliderThumbnails: NodeListOf<HTMLElement>;
    private sliderFullscreenButton: HTMLElement;
    private sliderCloseButton: HTMLElement;
    private sliderThumbnailsPrevButton: HTMLElement;
    private sliderThumbnailsNextButton: HTMLElement;
    private navigationElement: HTMLElement;

    constructor(slider: HTMLElement) {
        super(slider);

        this.sliderFullscreenButton = this.sliderElement.querySelector(
            SliderFullscreenComponent.FULLSCREEN_BUTTON_SELECTOR,
        ) as HTMLElement;
        this.sliderCloseButton = this.sliderElement.querySelector(
            SliderFullscreenComponent.CLOSE_BUTTON_SELECTOR,
        ) as HTMLElement;

        this.sliderThumbnailsElement = this.sliderElement.nextElementSibling as HTMLElement;
        this.sliderThumbnailsContainer = this.sliderThumbnailsElement.querySelector(
            SliderFullscreenComponent.THUMBNAILS_CONTAINER_SELECTOR,
        ) as HTMLElement;
        this.sliderThumbnails = this.sliderThumbnailsContainer.querySelectorAll(
            SliderFullscreenComponent.THUMBNAIL_SELECTOR,
        );

        this.sliderThumbnailsPrevButton = this.sliderThumbnailsElement.querySelector(
            SliderFullscreenComponent.THUMBNAILS_PREV_BUTTON_SELECTOR,
        ) as HTMLElement;
        this.sliderThumbnailsNextButton = this.sliderThumbnailsElement.querySelector(
            SliderFullscreenComponent.THUMBNAILS_NEXT_BUTTON_SELECTOR,
        ) as HTMLElement;
        this.navigationElement = document.querySelector(
            SliderFullscreenComponent.NAVIGATION_SELECTOR,
        ) as HTMLElement;

        this.addEventListeners();

        this.setThumbnailIndexes();

        this.updateThumbnails();
    }

    protected createSwiper(): void {
        this.swiperInstance = new Swiper(
            this.sliderElement,
            {
                navigation: {
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: false,
                },
                preloadImages: false,
                lazy: {
                    loadPrevNext: true,
                },
                slidesPerView: "auto",
                centeredSlides: true,
                spaceBetween: 20,
            } as any,
        ) as ISwiper;
    }

    private setThumbnailIndexes(): void {
        Array.from(this.sliderThumbnails).forEach((item, index) => {
            item.dataset.index = `${index}`;
        });
    }

    private setThumbnailContainerWidth(): void {
        let width = 5;
        Array.from(this.sliderThumbnails).forEach((item) => {
            width += (item.clientWidth + 5);
        });

        this.sliderThumbnailsContainer.style.width = `${width}px`;
    }

    private setActiveThumbnailInView(): void {
        const thumbnail = this.sliderThumbnailsContainer.querySelector(".is--active") as HTMLElement;

        if (inViewport(thumbnail, this.sliderThumbnailsContainer)) {
            return;
        }

        this.scrollTo(thumbnail.offsetLeft, "instant");
    }

    private updateThumbnails(): void {
        const currentIndex = this.swiperInstance.realIndex;
        const previousIndex = this.swiperInstance.previousIndex;

        const currentThumbnail = this.sliderThumbnails[currentIndex];
        currentThumbnail.classList.add("is--active");

        if (previousIndex !== undefined) {
            const previousThumbnail = this.sliderThumbnails[previousIndex];
            previousThumbnail.classList.remove("is--active");
        }

        if (inViewport(currentThumbnail, this.sliderThumbnailsContainer)) {
            return;
        }

        if (currentIndex > previousIndex) {
            this.scrollRight();
        } else if (currentIndex < previousIndex) {
            this.scrollLeft();
        }
    }

    private openFullscreen(): void {
        this.sliderElement.classList.add("is--fullscreen");
        this.navigationElement.style.display = "none";
        setTimeout(() => {
            this.updateSwiper();
            this.setThumbnailContainerWidth();
            this.setActiveThumbnailInView();
        });
    }

    private closeFullscreen(): void {
        this.sliderElement.classList.remove("is--fullscreen");
        this.navigationElement.style.display = "block";
        setTimeout(() => {
            this.updateSwiper();
        });
    }

    private updateSwiper(): void {
        this.swiperInstance.update();
    }

    private scrollLeft(): void {
        const scrollOffset = this.sliderThumbnailsElement.clientWidth / 4;
        const scrollLeft = this.sliderThumbnailsElement.scrollLeft - scrollOffset;

        this.scrollTo(scrollLeft);
    }

    private scrollRight(): void {
        const scrollOffset = this.sliderThumbnailsElement.clientWidth / 4;
        const scrollLeft = this.sliderThumbnailsElement.scrollLeft + scrollOffset;

        this.scrollTo(scrollLeft);
    }

    private scrollTo(offset: number, behavior: "smooth" | "auto" | "instant" | undefined = "smooth"): void {
        this.sliderThumbnailsElement.scrollTo({
            left: offset,
            behavior,
        });
    }

    private slideToIndex(e: Event): void {
        const target = e.currentTarget as HTMLElement;
        const index = parseInt(target.dataset.index as string, 10);

        this.swiperInstance.slideTo(index);
    }

    // Event listeners
    private addEventListeners(): void {
        this.sliderFullscreenButton.addEventListener("click", this.openFullscreen.bind(this));
        this.sliderCloseButton.addEventListener("click", this.closeFullscreen.bind(this));

        this.sliderThumbnailsPrevButton.addEventListener("click", this.scrollLeft.bind(this));
        this.sliderThumbnailsNextButton.addEventListener("click", this.scrollRight.bind(this));

        this.swiperInstance.on("slideChange", this.updateThumbnails.bind(this));

        Array.from(this.sliderThumbnails).forEach((item) => {
            item.addEventListener("click", this.slideToIndex.bind(this));
        });
    }
}



// WEBPACK FOOTER //
// ./components/slider/slider-fullscreen.component.ts